<script setup lang="ts">
import { useDisplay } from 'vuetify'
import {
  AgreementNotification,
  LogoCard,
  InterviewingCard,
  NeedHelpCard,
  AddSubjectsCard,
  AddStudentsCard,
  LicenceOverviewCard,
  ChecklistCard,
  ReportBuilderCard,
} from '@/components/dashboard'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useAgreementStore } from '@/stores/agreement'
import { UserProfilePermissionLevel } from '@/enums/UserProfilePermissionLevel'
import { checkPermissions } from '@/composable/useCheckPermissions'
import NewMyHQWelcomeModal from '@/components/app/NewMyHQWelcomeModal.vue'

const appStore = useAppStore(),
  authStore = useAuthStore(),
  agreementStore = useAgreementStore()

const { organisationIsBusinessPartnerClient } = storeToRefs(authStore)
const { smAndDown, mdAndUp } = useDisplay()

onMounted(() => {
  if(
    authStore.organisation?.myhq_version === 'ask'
    && checkPermissions(UserProfilePermissionLevel.ADMIN)
    && authStore.user_type !== 'admin'
  ){
    appStore.showMyHQWelcomeModal = true
  }

  // Load agreement data if needed for AgreementNotification component
  // TODO: Replace with VueQuery
  if(
    checkPermissions(UserProfilePermissionLevel.ADMIN)
    && !organisationIsBusinessPartnerClient.value
    && authStore.organisation?.customer_agreement_id
  ){
    agreementStore.getAgreement()
  }
})
</script>

<template>
  <VContainer
    class="dashboard mb-4"
    fluid
  >
    <AgreementNotification
      v-if="checkPermissions(UserProfilePermissionLevel.ADMIN) && !organisationIsBusinessPartnerClient"
    />
    <!-- Row with 1/2 cards -->
    <VRow>
      <VCol
        cols="12"
        lg="6"
        class="dashboard__first-row"
        :class="mdAndUp ? 'dashboard__first-row--md' : ''"
      >
        <LogoCard />
      </VCol>
      <VCol
        cols="12"
        lg="6"
        class="dashboard__first-row"
        :class="[smAndDown ? 'dashboard__first-row--sm' : '', mdAndUp ? 'dashboard__first-row--md' : '']"
      >
        <NeedHelpCard />
      </VCol>
    </VRow>
    <!-- Responsive row of 1/3 cards -->
    <VRow>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.ADMIN)"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <ChecklistCard />
      </VCol>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.STUDENT_MANAGER)"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <AddStudentsCard />
      </VCol>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.STUDENT_MANAGER)"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <AddSubjectsCard />
      </VCol>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.STUDENT_MANAGER)"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <LicenceOverviewCard />
      </VCol>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.STUDENT_MANAGER)"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <ReportBuilderCard on-dashboard />
      </VCol>
      <VCol
        v-if="checkPermissions(UserProfilePermissionLevel.STUDENT_MANAGER) && !organisationIsBusinessPartnerClient"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <InterviewingCard />
      </VCol>
    </VRow>
    <NewMyHQWelcomeModal />
  </VContainer>
</template>

<style lang="scss" scoped>
.dashboard__first-row {

  &--sm {
    max-height: 100%;
  }

  &--md {
    max-height: 15rem;
  }
}
</style>

<route lang="yaml">
name: index
alias: '/dashboard'
meta:
  requiresAuth: true
  restrictedForInterviewer: false
  title: page_titles.dashboard
</route>
